import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckLine, DownSmallLine } from '@mingcute/react';

import { cn } from '../lib/cn';
import { useMediaQuery } from '../hooks/use-media-query';

export interface StepperStep {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
}

export interface StepperProps extends React.HTMLAttributes<HTMLOListElement> {
  steps: StepperStep[];
  /** 1-based index of the current (in-progress) step. */
  current: number;
  /**
   * `auto` (default) renders the horizontal rail on desktop and a COMPACT
   * progress header on phones — see {@link CompactStepper}. Force `vertical` to
   * get the full stacked rail on mobile instead.
   */
  orientation?: 'horizontal' | 'vertical' | 'auto';
  /** When provided, steps at or below `maxClickableStep` become clickable. */
  onStepClick?: (step: number) => void;
  /** 1-based highest step that may be clicked (e.g. the furthest completed step). Defaults to `current`. */
  maxClickableStep?: number;
}

/**
 * Phone rendering of the stepper: one 44px row — "Krok 3 z 6 · Sekvence" — over a
 * progress bar, expanding on tap to the full list for jumping back.
 *
 * The stacked vertical rail this replaces was correct but far too expensive on a
 * phone: the campaign wizard's six steps put ~260px of navigation chrome above
 * the first field on EVERY step, so the form began below the fold throughout.
 * Collapsed by default costs ~60px and keeps the jump-back affordance one tap
 * away, which is the only thing the full rail was buying.
 */
function CompactStepper({
  steps,
  current,
  onStepClick,
  maxClickable,
  className,
}: {
  steps: StepperStep[];
  current: number;
  onStepClick?: (step: number) => void;
  maxClickable: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const total = steps.length;
  const currentStep = steps[current - 1];
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  const canExpand = Boolean(onStepClick);

  // Collapse whenever the step changes, so advancing never leaves the list open
  // over the form the user just moved to.
  React.useEffect(() => {
    setOpen(false);
  }, [current]);

  return (
    <div
      className={cn(
        'rounded-[var(--tvl-r2)] border border-[var(--tvl-hairline)] bg-[var(--tvl-sunken)]',
        className,
      )}
    >
      <button
        type="button"
        onClick={canExpand ? () => setOpen((v) => !v) : undefined}
        aria-expanded={canExpand ? open : undefined}
        // Not a button when there is nowhere to jump to — an inert control that
        // does nothing on tap is worse than plain text.
        {...(canExpand ? {} : { tabIndex: -1, 'aria-hidden': false })}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-2 text-left',
          canExpand ? 'active:bg-[var(--tvl-wash-pressed)]' : 'cursor-default',
        )}
      >
        <span className="shrink-0 text-xs font-[600] text-[var(--tvl-ink-3)]">
          {t('stepper.step_n_of_m', {
            n: current,
            m: total,
            defaultValue: 'Krok {{n}} z {{m}}',
          })}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-[500] text-[var(--tvl-ink)]">
          {currentStep?.title}
        </span>
        {canExpand ? (
          <DownSmallLine
            size={18}
            aria-hidden
            className={cn(
              'shrink-0 text-[var(--tvl-ink-3)] transition-transform',
              open && 'rotate-180',
            )}
          />
        ) : null}
      </button>

      <div
        className="mx-3 mb-2 h-1 overflow-hidden rounded-full bg-[var(--tvl-hairline)]"
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-label={t('stepper.progress', 'Průběh')}
      >
        <div
          className="h-full rounded-full bg-[var(--tvl-accent)] transition-[width] duration-[var(--tvl-d-slow)]"
          style={{ width: `${pct}%` }}
        />
      </div>

      {open ? (
        <ol className="border-t border-[var(--tvl-hairline)] p-1">
          {steps.map((s, idx) => {
            const n = idx + 1;
            const done = n < current;
            const isCurrent = n === current;
            const clickable = Boolean(onStepClick) && n <= maxClickable && !isCurrent;
            return (
              <li key={idx}>
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={clickable ? () => onStepClick!(n) : undefined}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-[var(--tvl-r2)] px-2 py-2 text-left',
                    isCurrent && 'bg-[var(--tvl-selected)]',
                    clickable && 'active:bg-[var(--tvl-wash-pressed)]',
                    !clickable && !isCurrent && 'opacity-45',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold',
                      done && 'border-[var(--tvl-accent)] bg-[var(--tvl-accent)] text-[var(--tvl-on-accent)]',
                      isCurrent && 'border-[var(--tvl-accent)] text-[var(--tvl-accent)]',
                      !done && !isCurrent && 'border-[var(--tvl-hairline)] text-[var(--tvl-ink-3)]',
                    )}
                  >
                    {done ? <CheckLine size={12} /> : n}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--tvl-ink)]">
                    {s.title}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      ) : null}
    </div>
  );
}

export const Stepper = React.forwardRef<HTMLOListElement, StepperProps>(
  (
    { className, steps, current, orientation = 'auto', onStepClick, maxClickableStep, ...props },
    ref,
  ) => {
    const maxClickable = maxClickableStep ?? current;
    const isMobile = !useMediaQuery('(min-width: 768px)');

    // `auto` on a phone gets the compact header rather than the stacked rail;
    // an explicit `vertical` still means vertical everywhere.
    if (orientation === 'auto' && isMobile) {
      return (
        <CompactStepper
          steps={steps}
          current={current}
          onStepClick={onStepClick}
          maxClickable={maxClickable}
          className={className}
        />
      );
    }

    const resolved = orientation === 'auto' ? 'horizontal' : orientation;
    const isVertical = resolved === 'vertical';

    return (
      <ol
        ref={ref}
        className={cn(
          'flex w-full',
          isVertical ? 'flex-col gap-3' : 'flex-row items-start gap-2',
          className,
        )}
        aria-label="Progress"
        {...props}
      >
        {steps.map((step, idx) => {
          const stepNum = idx + 1;
          const status: 'complete' | 'current' | 'upcoming' =
            stepNum < current
              ? 'complete'
              : stepNum === current
                ? 'current'
                : 'upcoming';
          const isLast = idx === steps.length - 1;

          const indicator = (
            <span
              className={cn(
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                status === 'complete' &&
                  'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--tvl-on-accent)]',
                status === 'current' &&
                  'border-[var(--color-accent)] text-[var(--color-accent)] bg-[var(--color-accent-subtle)]',
                status === 'upcoming' &&
                  'border-[var(--color-border)] text-[var(--color-fg-muted)] bg-[var(--color-surface)]',
              )}
              aria-hidden="true"
            >
              {step.icon ? (
                step.icon
              ) : status === 'complete' ? (
                <CheckLine size={14} />
              ) : (
                stepNum
              )}
            </span>
          );

          const labels = (
            <div className="flex flex-col min-w-0">
              <span
                className={cn(
                  'text-sm font-medium truncate',
                  status === 'upcoming'
                    ? 'text-[var(--color-fg-secondary)]'
                    : 'text-[var(--color-fg)]',
                )}
              >
                {step.title}
              </span>
              {step.description ? (
                <span className="text-xs text-[var(--color-fg-muted)] truncate">
                  {step.description}
                </span>
              ) : null}
            </div>
          );

          const connector = !isLast ? (
            <span
              aria-hidden
              className={cn(
                'bg-[var(--color-border)]',
                isVertical
                  ? 'absolute left-3.5 top-7 h-[calc(100%-1.75rem)] w-px'
                  : 'mx-1 mt-3 h-px flex-1',
                status === 'complete' && 'bg-[var(--color-accent)]',
              )}
            />
          ) : null;

          const clickable = Boolean(onStepClick) && stepNum <= maxClickable && stepNum !== current;
          const rowInner = (
            <>
              {indicator}
              {labels}
            </>
          );
          const row = clickable ? (
            <button
              type="button"
              onClick={() => onStepClick!(stepNum)}
              aria-label={`Step ${stepNum}${typeof step.title === 'string' ? `: ${step.title}` : ''}`}
              className={cn(
                'flex min-w-0 items-start gap-3 rounded-[var(--radius-md)] p-1 text-left',
                'hover:bg-[var(--color-subtle)] focus-visible:outline-none',
              )}
            >
              {rowInner}
            </button>
          ) : (
            <div className="flex min-w-0 items-start gap-3">{rowInner}</div>
          );

          return (
            <li
              key={idx}
              className={cn(
                'flex min-w-0',
                isVertical ? 'relative gap-3 pb-3' : 'flex-1 items-start',
              )}
              aria-current={status === 'current' ? 'step' : undefined}
            >
              {isVertical ? (
                <>
                  {row}
                  {connector}
                </>
              ) : (
                <div className="flex flex-1 items-start gap-2 min-w-0">
                  {row}
                  {connector}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    );
  },
);
Stepper.displayName = 'Stepper';
